import React, { useState } from 'react'
import { DayAvailability, DomicileAddress } from '../../types/preferences.types'
import type { PrivateAppointment } from '@/features/admin/tabs/mon-espace/types/monEspace.types'
import { AvailabilityCalendar } from './AvailabilityCalendar'
import './AvailabilitySection.styles.css'

interface AvailabilitySectionProps {
  availability: Record<string, DayAvailability>
  domiciles: DomicileAddress[]
  onChange: (availability: Record<string, DayAvailability>) => void
}

export const AvailabilitySection: React.FC<AvailabilitySectionProps> = ({ 
  availability, 
  domiciles, 
  onChange 
}) => {
  // Store private appointments in local state for now
  // In production, this would be part of the preferences API
  const [privateAppointments, setPrivateAppointments] = useState<PrivateAppointment[]>([])

  return (
    <div className="availability-section">
      <div className="section-header">
        <h2 className="section-title">Disponibilités et Planning</h2>
        <p className="section-description">
          Gérez vos heures de travail, vos rendez-vous privés et optimisez vos déplacements
        </p>
      </div>
      
      <div className="availability-content">
        <AvailabilityCalendar
          availability={availability}
          domicileAddresses={domiciles}
          onAvailabilityChange={onChange}
          privateAppointments={privateAppointments}
          onPrivateAppointmentsChange={setPrivateAppointments}
        />
      </div>
      
      <div className="availability-info">
        <div className="info-card">
          <h4>Comment ça marche ?</h4>
          <ul>
            <li>Définissez vos heures de travail pour chaque jour de la semaine</li>
            <li>Ajoutez vos rendez-vous privés avec les adresses de départ et d'arrivée</li>
            <li>Le système calculera automatiquement les temps de trajet nécessaires</li>
            <li>Vos expertises seront planifiées en tenant compte de vos contraintes</li>
          </ul>
        </div>
        
        <div className="stats-card">
          <h4>Résumé de la semaine</h4>
          <div className="stats-grid">
            <div className="stat">
              <span className="stat-value">{calculateTotalWorkingHours(availability)}h</span>
              <span className="stat-label">Heures de travail</span>
            </div>
            <div className="stat">
              <span className="stat-value">{privateAppointments.length}</span>
              <span className="stat-label">Rendez-vous privés</span>
            </div>
            <div className="stat">
              <span className="stat-value">{calculateAvailableSlots(availability, privateAppointments)}</span>
              <span className="stat-label">Créneaux disponibles</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper functions
function calculateTotalWorkingHours(availability: Record<string, DayAvailability>): number {
  let total = 0
  Object.values(availability).forEach(day => {
    if (!day) return
    
    // Handle slots array
    const slots = day.slots || []
    
    if (Array.isArray(slots)) {
      slots.forEach(slot => {
        if (slot?.enabled && slot?.start && slot?.end) {
          const start = new Date(`2000-01-01 ${slot.start}`)
          const end = new Date(`2000-01-01 ${slot.end}`)
          const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60)
          if (!isNaN(hours) && hours > 0) {
            total += hours
          }
        }
      })
    }
  })
  return Math.round(total)
}

function calculateAvailableSlots(
  availability: Record<string, DayAvailability>, 
  appointments: PrivateAppointment[]
): number {
  // Simplified calculation - in production this would be more sophisticated
  let slots = 0
  Object.values(availability).forEach(day => {
    if (!day) return
    
    // Handle slots array
    const daySlots = day.slots || []
    
    if (Array.isArray(daySlots)) {
      daySlots.forEach(slot => {
        if (slot?.enabled) slots++
      })
    }
  })
  return Math.max(0, slots - appointments.length)
}